import React, { FC, useRef, useState } from 'react';
import { Control, useController, UseFormSetValue, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, SearchIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../components/combobox/index';
import { Flag, FlagCode } from '../../../components/flag/index';
import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';
import { useAppQuery, useLocale } from '../../../hooks/index';
import { ApiService } from '../../../services/index';
import { Country } from '../../../types/country.types';
import { FormSchema } from './stakeholder-actions.validation';

export type SelectedNationality = {
  iso2Code: string;
  name: string;
};

export type NationalityItemsListProps = {
  control: Control<FormSchema>;
  data: Country[];
  isFetching: boolean;
  isLoading: boolean;
  setFormData: UseFormSetValue<FormSchema>;
};

const NationalityItemsList: FC<NationalityItemsListProps> = ({
  data,
  control,
  isFetching,
  isLoading,
  setFormData,
}) => {
  const { stepOne } = useWatch<FormSchema>({ control });
  if (!data || isFetching || isLoading) return <span>Loading ...</span>;

  return (
    <>
      {stepOne?.human?.nationality && (
        <span
          className="cursor-pointer gap-[6px] rounded px-3 py-1.5 text-sm text-gray-700 hover:cursor-pointer hover:bg-brand-25"
          onClick={() => setFormData('stepOne.human.nationality', undefined)}
        >
          Reset
        </span>
      )}
      {data
        .sort((a, b) => a.nationality.localeCompare(b.nationality))
        .map(({ iso2, nationality }) => (
          <FormComboboxOption
            control={control}
            key={iso2}
            name="stepOne.human.nationality"
            value={{ iso2Code: iso2, name: nationality }}
          >
            <Flag countryCode={iso2.toUpperCase() as FlagCode} />
            <span className="text-sm text-gray-700">{nationality}</span>
          </FormComboboxOption>
        ))}
    </>
  );
};

export type NationalityComboboxProps = {
  control: Control<FormSchema>;
  selectedNationality?: SelectedNationality;
  setFormData: UseFormSetValue<FormSchema>;
};

const NationalityCombobox: FC<NationalityComboboxProps> = ({
  control,
  selectedNationality,
  setFormData,
}) => {
  const [search, setSearch] = useState('');
  const { formState } = useController({
    control,
    name: 'stepOne.human.nationality',
  });
  const { locale } = useLocale();
  const {
    data: countryData,
    isLoading,
    isFetching,
  } = useAppQuery<Country[]>({
    queryKey: [QueryKey.GET_COUNTRY, { search }],
    queryFn: () =>
      ApiService.get({ endpoint: BackendRoute.COUNTRIES }, { queryParams: { search }, locale }),
  });
  const ref = useRef<HTMLInputElement>(null);
  return (
    <FormCombobox
      className="relative"
      control={control}
      name={'stepOne.human.nationality'}
      onClose={() => setSearch('')}
      onSelect={() => {
        setSearch('');
      }}
    >
      <div className="relative">
        <FormComboboxInput
          className={'bg-gray-900'}
          control={control}
          customValue={(value) => value?.name || ''}
          icon={<ChevronDownIcon className={twMerge('mt-3', formState.disabled && 'hidden')} />}
          iconBeforeInput={
            <Flag
              className="mt-5 w-4 shrink-0"
              countryCode={selectedNationality?.iso2Code as FlagCode}
            />
          }
          name={'stepOne.human.nationality'}
          placeholder={<AppFormattedMessage id="NATIONALITY_OPTIONAL" />}
          readOnly
          wrapperClassName={formState.disabled && 'border-none'}
        />
        <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" />
      </div>
      <ComboboxOptions className="w-full">
        <div className="relative flex items-center" onClick={() => ref.current?.focus()}>
          <SearchIcon className="absolute ml-[6px]" />
          <input
            className="w-full rounded border-[2px] border-transparent bg-gray-50 p-[6px] pl-8 text-gray-700 outline-none placeholder:text-gray-400 focus:border-brand-700 focus:bg-white"
            onChange={({ target: { value } }) => setSearch(value)}
            ref={ref}
            value={search}
          />
        </div>

        <NationalityItemsList
          control={control}
          data={countryData || []}
          isFetching={isFetching}
          isLoading={isLoading}
          setFormData={setFormData}
        />
      </ComboboxOptions>
    </FormCombobox>
  );
};

export default NationalityCombobox;
